import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import { useAppDispatch } from '~/redux/hooks'
import {
  resetCorrectConsecutiveAnswersActionCreator,
  setAnswerStateActionCreator,
} from '~/bootcamp/slices/bootcamp-activity.slice'
import Button from '~/components/Buttons/Button'
import ResponsiveImage from '~/components/Image/ResponsiveImage'

import { useGetBestAccuracyQuery } from '~/bootcamp/apis/get-accuracy.api'
import Loader from '~/components/Utility/Loader'
import useIsBootcampGradePage from '~/bootcamp/hooks/useIsBootcampGradePage'
import { GRADE_QUERY_PARAM } from '~/bootcamp/constants/bootcamp.const'
import useNextActivityType from '~/bootcamp/hooks/useNextActivityType'
import SetCompletedAvatar from '~/bootcamp/pages/BootcampView/BootcampContent/SetCompletedAvatar'
import SetCompletedScore from '~/bootcamp/pages/BootcampView/BootcampContent/SetCompletedScore'

import { BackToCurriculumPage, NextLevelText } from './NavigationButton'

function ActivitySetCompleted(): React.ReactElement {
  const { t } = useTranslation('bootcamp')
  const router = useRouter()
  const { asPath } = router
  const dispatch = useAppDispatch()
  const isGradePage = useIsBootcampGradePage()

  const asPathWithoutQuery = asPath.split('?')[0]

  const { data, isLoading } = useGetBestAccuracyQuery({ path: asPathWithoutQuery })

  const bestAccuracy = data ? data.accuracy : 0

  const nextActivityType = useNextActivityType()

  const changeActivityType = useCallback((): void => {
    if (nextActivityType > 0) {
      const urlParts = asPathWithoutQuery.split('/')
      urlParts[urlParts.length - 1] = nextActivityType.toString()
      const nextActivityTypeQueryParam = isGradePage
        ? {
            [GRADE_QUERY_PARAM]: isGradePage,
          }
        : {}

      router.replace({ pathname: urlParts.join('/'), query: nextActivityTypeQueryParam }, undefined, {
        shallow: true,
        scroll: false,
      })
      dispatch(resetCorrectConsecutiveAnswersActionCreator())
      dispatch(setAnswerStateActionCreator(null)) // Reset answer state, null means not attempted.
    }
  }, [asPathWithoutQuery, dispatch, isGradePage, nextActivityType, router])

  const nextButton = useMemo(() => {
    if (nextActivityType > 0) {
      return (
        <Button
          className="!w-full border-2 font-semibold text-white bg-purple border-purple"
          onClick={() => changeActivityType()}
          size="medium"
        >
          {t('nextActivity')}
        </Button>
      )
    }

    if (isGradePage) {
      return <BackToCurriculumPage className="text-white border-2 bg-purple border-purple" />
    } else {
      return (
        <NextLevelText
          className="!w-full border-2 font-semibold text-white hover:scale-110 duration-300 bg-purple border-purple"
          label={t('nextLevel')}
        />
      )
    }
  }, [changeActivityType, isGradePage, nextActivityType, t])

  return (
    <Loader className="mx-auto mt-44" isLoading={isLoading}>
      <div className="absolute left-0 right-0 mx-auto top-0 flex flex-col items-center space-y-4 bg-purple-darker text-white w-full h-full p-4 overflow-hidden">
        <div className="relative flex flex-col justify-center items-center w-full h-full">
          <ResponsiveImage
            height={400}
            imageClassName="absolute left-0 right-0 m-auto -top-8 bottom-0"
            srcKey="bootcamp/reward-chest-background.svg"
            width={400}
          />
          <SetCompletedAvatar />
        </div>
        <SetCompletedScore correctPercent={bestAccuracy} nextButton={nextButton} />
      </div>
    </Loader>
  )
}

export default ActivitySetCompleted
